import { useCallback, useState } from "react";
import { useAuthUser } from "src/auth/useAuth";
import { requestNotificationPermission } from "src/components/NotificationCenter/browserNotifications";
import { usePersistedFlag } from "src/hooks/usePersistedFlag";

const supportsNotifications = () => typeof window !== "undefined" && "Notification" in window;

/**
 * User-level notification display preferences.
 *
 * Both are presentation only — the notifications websocket stays subscribed and
 * notifications keep landing in the store either way, so badges, the chat modal
 * and cross-tab dismiss sync are unaffected. Persisted to localStorage per user
 * so they survive reloads and sign-outs.
 */
export function useNotificationPreferences() {
  const { user } = useAuthUser();
  const key = user?.id ?? "anon";

  const [cardsHidden, setCardsHidden] = usePersistedFlag(`notification-cards-hidden:${key}`, false);
  const [browserDisabled, setBrowserDisabled] = usePersistedFlag(
    `browser-notifications-disabled:${key}`,
    false
  );

  const [permission, setPermission] = useState<NotificationPermission | "unsupported">(() =>
    supportsNotifications() ? Notification.permission : "unsupported"
  );

  const setShowCards = useCallback((show: boolean) => setCardsHidden(!show), [setCardsHidden]);

  // Turning the toggle on is the natural moment to ask for OS permission — the
  // user just expressed intent, so the prompt isn't a drive-by.
  const setBrowserNotificationsEnabled = useCallback(
    async (enabled: boolean) => {
      setBrowserDisabled(!enabled);
      if (enabled && supportsNotifications() && Notification.permission === "default") {
        await requestNotificationPermission();
        setPermission(Notification.permission);
      }
    },
    [setBrowserDisabled]
  );

  return {
    showCards: !cardsHidden,
    setShowCards,
    // A browser-level block wins over the stored preference: there's nothing we
    // can show, so report it as off rather than lying in the settings UI.
    browserNotificationsEnabled: !browserDisabled && permission !== "denied" && permission !== "unsupported",
    setBrowserNotificationsEnabled,
    browserPermission: permission,
  };
}
